import { useEffect, useRef, useState } from "react";
import { DollarSign } from "lucide-react";

/**
 * TipPicker — single-row selector: 18% / 20% / 22% / Custom / No Tip.
 * Defaults to 18% on first mount. Emits a plain dollar amount to the
 * parent via `onChange`; parent owns the state of what's charged to Square.
 *
 * Contract:
 *   subtotal (number, USD)  → the net subtotal (post-reward) to compute % against
 *   value    (number, USD)  → currently selected tip in dollars (for display sync)
 *   onChange (fn)           → called with the new tip dollars whenever the pick changes
 */
export default function TipPicker({ subtotal, value = 0, onChange }) {
  const [mode, setMode] = useState("20");
  const [customStr, setCustomStr] = useState("");
  const didInit = useRef(false);

  const PRESETS = [
    { key: "18", label: "18%", pct: 0.18 },
    { key: "20", label: "20%", pct: 0.20 },
    { key: "22", label: "22%", pct: 0.22 },
  ];

  // Default to 20% once we have a subtotal to compute against, and only ever
  // do it once so we don't clobber the customer's later choice.
  useEffect(() => {
    if (didInit.current) return;
    if (!subtotal || subtotal <= 0) return;
    didInit.current = true;
    const dollars = Math.round(subtotal * 0.20 * 100) / 100;
    onChange(dollars);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subtotal]);

  const pickPct = (key, pct) => {
    setMode(key);
    setCustomStr("");
    const dollars = Math.round(subtotal * pct * 100) / 100;
    onChange(dollars);
  };

  const pickNone = () => {
    setMode("none");
    setCustomStr("");
    onChange(0);
  };

  const pickCustom = () => {
    setMode("custom");
    const dollars = customStr ? Math.max(0, parseFloat(customStr) || 0) : 0;
    onChange(Math.round(dollars * 100) / 100);
  };

  const onCustomChange = (raw) => {
    const clean = raw.replace(/[^\d.]/g, "");
    const parts = clean.split(".");
    const normalized =
      parts.length > 1 ? `${parts[0]}.${parts[1].slice(0, 2)}` : clean;
    setCustomStr(normalized);
    const dollars = Math.max(0, Math.min(1000, parseFloat(normalized) || 0));
    onChange(Math.round(dollars * 100) / 100);
  };

  const baseBtn =
    "py-2.5 px-1 rounded-lg border-2 font-black uppercase tracking-wider text-[11px] leading-tight transition-all active:scale-95 flex flex-col items-center justify-center min-h-[54px]";

  return (
    <section
      data-testid="tip-picker"
      className="bg-white border border-stone-200 rounded-xl p-4 space-y-3"
    >
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-widest text-stone-600 font-black">
          Add a Tip
        </div>
        {value > 0 && (
          <div
            data-testid="tip-picker-current"
            className="font-display text-brand-red text-lg leading-none"
          >
            ${value.toFixed(2)}
          </div>
        )}
      </div>

      <div className="grid grid-cols-5 gap-1.5">
        {PRESETS.map(({ key, label, pct }) => {
          const isActive = mode === key;
          const previewDollars = Math.round(subtotal * pct * 100) / 100;
          return (
            <button
              key={key}
              type="button"
              onClick={() => pickPct(key, pct)}
              data-testid={`tip-picker-${key}`}
              className={`${baseBtn} ${
                isActive
                  ? "bg-brand-red text-white border-brand-red shadow"
                  : "bg-white text-stone-700 border-stone-300 hover:border-brand-red"
              }`}
            >
              <div className="text-sm">{label}</div>
              <div className="text-[9px] font-normal opacity-80 mt-0.5">
                ${previewDollars.toFixed(2)}
              </div>
            </button>
          );
        })}
        <button
          type="button"
          onClick={pickCustom}
          data-testid="tip-picker-custom"
          className={`${baseBtn} ${
            mode === "custom"
              ? "bg-brand-yellow text-stone-900 border-stone-900"
              : "bg-white text-stone-700 border-stone-300 hover:border-brand-red"
          }`}
        >
          Custom
        </button>
        <button
          type="button"
          onClick={pickNone}
          data-testid="tip-picker-none"
          className={`${baseBtn} ${
            mode === "none" && value === 0
              ? "bg-stone-800 text-white border-stone-800"
              : "bg-white text-stone-700 border-stone-300 hover:border-stone-500"
          }`}
        >
          No Tip
        </button>
      </div>

      {mode === "custom" && (
        <div className="flex items-center gap-2 pt-1">
          <div className="flex-1 relative">
            <DollarSign className="w-4 h-4 text-stone-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
            <input
              type="text"
              inputMode="decimal"
              placeholder="0.00"
              value={customStr}
              onChange={(e) => onCustomChange(e.target.value)}
              data-testid="tip-picker-custom-input"
              className="w-full pl-9 pr-3 py-2.5 rounded-lg border-2 border-stone-300 focus:border-brand-red outline-none text-base font-mono"
              autoFocus
            />
          </div>
        </div>
      )}
    </section>
  );
}
